import type { AddressState } from '@paid-ui/enums/address';
import type { Address } from '@paid-ui/schemas/zod/address';

import { CountryCode } from '@paid-ui/enums/address';

/**
 * Format address object to string.
 *
 * @param address - Address object
 * @param options - Format options
 * @param options.suburbOnly - Only include suburb and state in the formatted address
 * @returns Address string
 */
export function formatAddress(
  address?: Address | null | '',
  options?: {
    suburbOnly?: boolean;
  },
): string {
  if (!address || !address.country || !address.state || !address.suburb || !address.streetName) {
    return '';
  }

  const { unitNumber, streetNumber, streetName, suburb, postcode, state } = address;

  const fullStreetNumber = [unitNumber, streetNumber].filter(Boolean).join('-');
  const fullStreetName = [fullStreetNumber, streetName].filter(Boolean).join(' ');

  if (!fullStreetName) {
    return '';
  }

  if (options?.suburbOnly) {
    return `${suburb}, ${state}`;
  }

  return `${fullStreetName}, ${suburb} ${state} ${postcode}`;
}

/**
 * Format Google Maps address components to address object.
 *
 * @param components - Google Maps address components
 * @returns Address object
 */
export function formatGoogleMapsComponents(
  components: google.maps.GeocoderAddressComponent[],
): Address | '' {
  const streetNumber =
    components.find((component) => component.types.includes('street_number'))?.short_name ?? '';

  if (!streetNumber) {
    throw new Error('No street number found in address');
  }

  const streetName =
    components.find((component) => component.types.includes('route'))?.long_name ?? '';

  if (!streetNumber) {
    throw new Error('No street name found in address');
  }

  const suburb =
    components.find(
      (component) =>
        component.types.includes('locality') || component.types.includes('colloquial_area'),
    )?.long_name ?? '';

  if (!suburb) {
    throw new Error('No suburb found in address');
  }

  const state = components.find((component) =>
    component.types.includes('administrative_area_level_1'),
  )?.short_name as AddressState | undefined;

  if (!state) {
    throw new Error('No state found in address');
  }

  const postcode =
    components.find((component) => component.types.includes('postal_code'))?.short_name ?? '';

  if (!postcode) {
    throw new Error('No postcode found in address');
  }

  const country = components.find((component) => component.types.includes('country'))
    ?.short_name as CountryCode | undefined;

  const unitNumber = components
    .find((component) => component.types.includes('subpremise'))
    ?.long_name?.replaceAll(/[Uu]nit\s/g, '');

  return {
    unitNumber,
    streetNumber,
    streetName,
    suburb,
    state,
    postcode,
    country: country ?? CountryCode.AU,
  };
}
